import { InputWrapper, Row, SPACING, LabelWrapper, useAppPalette, Text } from "@bridgesplit/ui";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { BsMetaUtil, DEFAULT_STRATEGY_DURATION, useTokenListMetadataByMint } from "@bridgesplit/abf-react";
import { AppDialog } from "app/utils";
import { ExpandMore } from "@mui/icons-material";

import { useMarketBorrowSetCollateral } from "./util";
import { useMarketBorrowContext } from "./MarketBorrowContext";
import {
    BalanceDisplay,
    NullTokenInput,
    TokenInputWithPrice,
    useBalanceChecker,
    GenericLpPositionSelect,
    TokenImage
} from "../../common";
import { useMarketContext } from "../common";
import { useMarketDialog } from "../util";
import { BorrowMode, MarketBorrowForm } from "./type";

const LABEL = "Your collateral";
export default function BorrowCollateral() {
    // need this for the orca collateral section
    return (
        <>
            <AmountInput />
            <LpPositionSelect />
        </>
    );
}

function AmountInput() {
    const { form, setForm, supportedCollateral, activeUserCollateral } = useMarketBorrowContext();
    const { open: openDialog } = useMarketDialog();
    const marketProps = useMarketContext();

    const setToken = useMarketBorrowSetCollateral();

    const metadata = useTokenListMetadataByMint(form.collateralMint);
    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: form.collateralMint ?? undefined,
        amount: form.collateralAmount,
        customBalance: activeUserCollateral?.amount
    });

    const selectToken = () => {
        openDialog(AppDialog.BorrowSelectCollateral, {
            setToken,
            marketProps,
            strategyDuration: form.strategyDuration ?? DEFAULT_STRATEGY_DURATION
        });
    };
    if (supportedCollateral?.length === 0) {
        return (
            <LabelWrapper label={LABEL} color="body">
                <Text color="disabled" variant="body2">
                    You don't own any supported collateral
                </Text>
            </LabelWrapper>
        );
    }

    if (!!metadata && BsMetaUtil.isNonFungible(metadata)) {
        return (
            <LabelWrapper label={LABEL} color="body">
                <NftSelect metadata={metadata} onClick={selectToken} />
            </LabelWrapper>
        );
    }

    function setMax(maxAmount: number | undefined) {
        setForm((prev) => ({
            ...prev,
            collateralAmount: maxAmount,
            refetchOutAmount: true,
            mode: BorrowMode.InputCollateral
        }));
    }

    if (form.collateralMint) {
        return (
            <TokenInputWithPrice
                label={LABEL}
                labelColor="body"
                metadata={metadata}
                selectToken={selectToken}
                value={form.collateralAmount}
                setValue={(collateralAmount) =>
                    setForm((prev) => ({
                        ...prev,
                        collateralAmount,
                        refetchOutAmount: true,
                        mode: BorrowMode.InputCollateral
                    }))
                }
                belowInput={
                    <BalanceDisplay
                        balance={balance}
                        metadata={metadata}
                        insufficientBalance={insufficientBalance}
                        isLoading={isLoading}
                        setMax={setMax}
                    />
                }
            />
        );
    }

    return <NullTokenInput onClick={selectToken} loading={form.collateralMint === undefined} />;
}

function NftSelect({ metadata, onClick }: { onClick: () => void; metadata: TokenListMetadata }) {
    const { hoverBackground } = useAppPalette();
    return (
        <InputWrapper
            onClick={onClick}
            sx={{
                height: SPACING * 6.5,
                py: 0,
                cursor: "pointer",
                ":hover": { background: hoverBackground },
                justifyContent: "space-between"
            }}
        >
            <Row spacing={1}>
                <TokenImage metadata={metadata} size="sm" />
                <Text>{BsMetaUtil.getName(metadata)}</Text>
            </Row>
            <Text variant="h1" color="caption">
                <ExpandMore />
            </Text>
        </InputWrapper>
    );
}

function LpPositionSelect() {
    const { form, setForm } = useMarketBorrowContext();

    if (!form.collateralMint) return null;

    return (
        <GenericLpPositionSelect<MarketBorrowForm> collateralMint={form.collateralMint} form={form} setForm={setForm} />
    );
}
